import { Card, CircularProgress } from '@mui/material';
import { DateRange } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { Colors } from '../../../../ui/theme/colors';
import { ErrorInline } from '../../../../ui/components/pure/errors/error-inline/error-inline.component';
import { Snackbar } from '../../../../ui/components/pure/snackbar/snackbar.component';
import { LockerTimelineGroup } from '../logic/locker-timeline.utils';
import { LockerTimelineGroupComponent } from './locker-timeline-group.component';
import { TextButton } from '../../../../ui/components/pure/text-button/text-button.component';
import { useLockerTimelineCardStyles } from './locker-timeline-card.styles';

interface LockerTimelineCardProps {
  groups: LockerTimelineGroup[];
  isLoading: boolean;
  isFetchingOlder: boolean;
  isFetchingNewer: boolean;
  isError: boolean;
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  loadOlder: () => void;
  loadNewer: () => void;
  hasOlderEntries: boolean;
  noNewerEventsFound: boolean;
  clearNoNewerEvents: () => void;
  projectID: string;
}

export function LockerTimelineCard({
  groups,
  isLoading,
  isFetchingOlder,
  isFetchingNewer,
  isError,
  selectedDate,
  setSelectedDate,
  loadOlder,
  loadNewer,
  hasOlderEntries,
  noNewerEventsFound,
  clearNoNewerEvents,
  projectID
}: LockerTimelineCardProps) {
  const classes = useLockerTimelineCardStyles();

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      setSelectedDate(date.toDate());
    }
  };

  return (
    <Card elevation={1} className={classes.card}>
      {isError ? (
        <ErrorInline
          open={true}
          error={{
            title: 'Something went wrong',
            message: 'Sorry, we encountered a problem getting the timeline. Please refresh or go back and try again'
          }}
        />
      ) : (
        <>
          <div className={classes.header}>
            <p className={classes.title}>Events</p>
            <DatePicker
              className={classes.datePicker}
              value={dayjs(selectedDate)}
              onChange={handleDateChange}
              format="DD/MM/YYYY"
              disableFuture
              label="View from date"
              closeOnSelect
              slots={{ openPickerIcon: DateRange }}
              slotProps={{
                openPickerIcon: { className: classes.calendarIcon },
                popper: { style: { zIndex: 1500 } },
                textField: {
                  variant: 'outlined',
                  size: 'small'
                }
              }}
            />
          </div>
          <div className={classes.buttonWrapper}>
            <TextButton
              color="secondary"
              fontColor={Colors.timelineGray}
              variant="outlined"
              size="small"
              onClick={loadNewer}
              disabled={isFetchingNewer}
              isLoading={isFetchingNewer}
            >
              Load newer
            </TextButton>
          </div>
          {isLoading ? (
            <div className={classes.empty}>
              <CircularProgress size={24} />
            </div>
          ) : groups.length === 0 ? (
            <div className={classes.empty}>No events found.</div>
          ) : (
            <div className={classes.groups}>
              {groups.map((group, index) => (
                <LockerTimelineGroupComponent
                  key={
                    group.type === 'session'
                      ? group.sessionId
                      : group.type === 'delivery'
                      ? `${group.deliveryId}-${index}`
                      : `ungrouped-${index}`
                  }
                  group={group}
                  projectID={projectID}
                />
              ))}
            </div>
          )}
          <div className={classes.buttonWrapper}>
            {hasOlderEntries ? (
              <TextButton
                color="secondary"
                fontColor={Colors.timelineGray}
                variant="outlined"
                size="small"
                onClick={loadOlder}
                disabled={isFetchingOlder}
                isLoading={isFetchingOlder}
              >
                Load older
              </TextButton>
            ) : (
              <span className={classes.noMoreEvents}>No older events</span>
            )}
          </div>
        </>
      )}
      {noNewerEventsFound && (
        <Snackbar severity="info" onClose={clearNoNewerEvents}>
          No newer events found
        </Snackbar>
      )}
    </Card>
  );
}
